import { createQueryKeys, inferQueryKeys } from '@lukemorales/query-key-factory';
import { AxiosRequestConfig, AxiosResponse } from 'axios';

import {
  $httpRootApi,
  AnyErrorResponse,
  queryClient,
  UpdateCacheCallback,
  updateQueryCache,
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
} from '@core/http-client';
import { GetSaleInsightsRequestPayload } from '@redux/types/insights/sale/saleInsightsTypes';
import { SavedViewType } from '@shared/models/saved_view/savedView';
import { getQueriesAsSearch } from '@shared/utils/common';
import { serializeBodyWithDetailsToFormData } from '@shared/utils/formdata-serializer';

import {
  SaleTransactionActivitiesIndexPayload,
  SaleTransactionActivitiesIndexResponse,
  SaleTransactionAtlasValidationErrorResponse,
  SaleTransactionCreateCommentPayload,
  SaleTransactionDeleteFilePayload,
  SaleTransactionPublishToAtlasPayload,
  SaleTransactionPublishToRadiusPayload,
  SaleTransactionRenameFilePayload,
  SaleTransactionReorderFilesPayload,
  SaleTransactionRequestSegments,
  SaleTransactionsBulkAssignUsersPayload,
  SaleTransactionsBulkChangeBuildingTypesPayload,
  SaleTransactionsBulkChangeTenurePayload,
  SaleTransactionsBulkDeletePayload,
  SaleTransactionsBulkPublishToAtlasPayload,
  SaleTransactionsBulkSetOurAcquisitionPayload,
  SaleTransactionsBulkSetOurDisposalPayload,
  SaleTransactionsBulkUpdateCompletionDatePayload,
  SaleTransactionsBulkUpdateGradeOfSpacePayload,
  SaleTransactionsBulkUpdateMeasurementTypePayload,
  SaleTransactionsBulkUpdatePossibleUseClassesPayload,
  SaleTransactionsBulkUpdateSectorsPayload,
  SaleTransactionsBulkUpdateStatusPayload,
  SaleTransactionsBulkUpdateTagsPayload,
  SaleTransactionsBulkUpdateTeamsPayload,
  SaleTransactionsBulkUpdateTypePayload,
  SaleTransactionsCreatePayload,
  SaleTransactionsListPayload,
  SaleTransactionsListResponse,
  SaleTransactionsShowItem,
  SaleTransactionsSpaceDetailsUpdatePayload,
  SaleTransactionsUpdatePayload,
  SaleTransactionUpdateAddressPayload,
  SaleTransactionValidatePublishPreCreateParams,
} from './transactionsSalesApi.types';

export const saleTransactionsQueryKeys = createQueryKeys('saleTransactions', {
  all: null,
  activitiesIndex: (payload: SaleTransactionActivitiesIndexPayload) => ({
    queryKey: [payload.saleTransactionId, payload.query],
    queryFn: async ({ signal }) =>
      (
        await $httpRootApi.get<SaleTransactionActivitiesIndexResponse>(
          `/activities/sale_transaction/${payload.saleTransactionId}${getQueriesAsSearch(payload.query)}`,
          { signal }
        )
      ).data,
  }),
  list: (payload: SaleTransactionsListPayload) => ({
    queryKey: [payload.viewType || '', payload.query],
    queryFn: async ({ signal }) => {
      const segment = payload.requestSegment ?? SaleTransactionRequestSegments.all;

      if (payload.viewType === SavedViewType.map) {
        return (
          await $httpRootApi.post<any, AxiosResponse<SaleTransactionsListResponse>>(
            `/transactions/sales${segment}/map`,
            payload.query,
            {
              signal,
            }
          )
        ).data;
      }

      // Default
      return (
        await $httpRootApi.get<SaleTransactionsListResponse>(
          `/transactions/sales${segment}${getQueriesAsSearch(payload.query)}`,
          {
            signal,
          }
        )
      ).data;
    },
  }),
  show: (id: Id) => ({
    queryKey: [id],
    queryFn: async () => (await $httpRootApi.get(`/transactions/sales/${id}`)).data,
  }),
  validateRadius: (id: string) => ({
    queryKey: [id],
    queryFn: async () => (await $httpRootApi.get(`/transactions/sales/${id}/validate_publish_to_radius`)).data,
  }),
});

export type saleTransactionsQueryKeys = inferQueryKeys<typeof saleTransactionsQueryKeys>;

// Raw query helpers (usable from sagas, etc.)
export const saleTransactionsQueries = {
  statistics: async (payload: GetSaleInsightsRequestPayload, config?: AxiosRequestConfig) =>
    $httpRootApi.get(`/transactions/sales/statistics${getQueriesAsSearch(payload)}`, config),
};

export const useSaleTransactionsListQuery = (
  payload: SaleTransactionsListPayload,
  options?: UseQueryOptions<SaleTransactionsListResponse>
) =>
  useQuery<saleTransactionsQueryKeys['list']['queryKey'], SaleTransactionsListResponse>({
    ...saleTransactionsQueryKeys.list(payload),
    keepPreviousData: true,
    ...options,
  });

export const useSaleTransactionsShowQuery = (id: string, options?: UseQueryOptions<SaleTransactionsShowItem>) =>
  useQuery<saleTransactionsQueryKeys['show']['queryKey'], SaleTransactionsShowItem>({
    ...saleTransactionsQueryKeys.show(id),
    ...options,
  });

export const useSaleTransactionsValidateRadiusQuery = (id: string, options?: UseQueryOptions<any, AnyErrorResponse>) =>
  useQuery<saleTransactionsQueryKeys['validateRadius']['queryKey'], any, AnyErrorResponse>({
    ...saleTransactionsQueryKeys.validateRadius(id),
    ...options,
  });

export const useSaleTransactionsCreateMutation = (opts?: UseMutationOptions<SaleTransactionsCreatePayload, any>) =>
  useMutation({
    ...opts,
    mutationFn: async (data) => {
      const formData = serializeBodyWithDetailsToFormData(data.body);

      return (
        await $httpRootApi.post<FormData>(`/transactions/sales`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
      ).data;
    },
  });

export const useSaleTransactionsUpdateMutation = (
  opts?: UseMutationOptions<SaleTransactionsUpdatePayload, SaleTransactionsShowItem>
) =>
  useMutation({
    ...opts,
    mutationFn: async (payload) =>
      (
        await $httpRootApi.put<SaleTransactionsUpdatePayload['body'], AxiosResponse<SaleTransactionsShowItem>>(
          `/transactions/sales/${payload.id}`,
          payload.body
        )
      ).data,
  });

export const useSaleTransactionCreateCommentMutation = (
  opts?: UseMutationOptions<SaleTransactionCreateCommentPayload, SaleTransactionsShowItem>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.post<SaleTransactionCreateCommentPayload['body'], AxiosResponse<SaleTransactionsShowItem>>(
          `/transactions/sales/${data.id}/comment`,
          data.body
        )
      ).data,
  });

export const useSaleTransactionUpdateAddressMutation = (
  opts?: UseMutationOptions<SaleTransactionUpdateAddressPayload, SaleTransactionsShowItem>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<SaleTransactionUpdateAddressPayload['body'], AxiosResponse<SaleTransactionsShowItem>>(
          `/transactions/sales/${data.id}/address`,
          data.body
        )
      ).data,
  });

export const useSaleTransactionsSpaceDetailsUpdateMutation = (
  opts?: UseMutationOptions<SaleTransactionsSpaceDetailsUpdatePayload, SaleTransactionsShowItem>
) =>
  useMutation({
    ...opts,
    mutationFn: async (payload) =>
      (
        await $httpRootApi.put<
          SaleTransactionsSpaceDetailsUpdatePayload['body'],
          AxiosResponse<SaleTransactionsShowItem>
        >(`/transactions/sales/${payload.id}/space-details`, payload.body)
      ).data,
  });

export const useSaleTransactionDelete = (opts?: UseMutationOptions<number, number>) =>
  useMutation({
    ...opts,
    mutationFn: async (id) => {
      await $httpRootApi.delete(`/transactions/sales/${id}`);

      return id;
    },
  });

export const useSaleTransactionPublishToRadiusMutation = (
  opts?: UseMutationOptions<SaleTransactionPublishToRadiusPayload, SaleTransactionsShowItem, AnyErrorResponse>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<SaleTransactionPublishToRadiusPayload['body'], AxiosResponse<SaleTransactionsShowItem>>(
          `transactions/sales/${data.id}/publish_to_radius`,
          data.body
        )
      ).data,
  });

export const useSaleTransactionPublishToAtlasMutation = (
  opts?: UseMutationOptions<SaleTransactionPublishToAtlasPayload, SaleTransactionsShowItem, AnyErrorResponse>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<SaleTransactionPublishToAtlasPayload['body'], AxiosResponse<SaleTransactionsShowItem>>(
          `transactions/sales/${data.id}/publish_to_atlas`,
          data.body
        )
      ).data,
  });

export const useSaleTransactionUpdateAtlasMutation = (
  opts?: UseMutationOptions<SaleTransactionPublishToAtlasPayload, SaleTransactionsShowItem, AnyErrorResponse>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<SaleTransactionPublishToAtlasPayload['body'], AxiosResponse<SaleTransactionsShowItem>>(
          `transactions/sales/${data.id}/update_atlas`,
          data.body
        )
      ).data,
  });

export const useSaleTransactionUnpublishAtlasMutation = (
  opts?: UseMutationOptions<{ id: string }, SaleTransactionsShowItem, AnyErrorResponse>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<void, AxiosResponse<SaleTransactionsShowItem>>(
          `transactions/sales/${data.id}/unpublish_atlas`
        )
      ).data,
  });

export const useSaleTransactionValidatePublishToAtlasQuery = (
  id: string | undefined,
  opts?: UseQueryOptions<SaleTransactionAtlasValidationErrorResponse, SaleTransactionAtlasValidationErrorResponse>
) =>
  useQuery({
    ...opts,
    enabled: opts?.enabled ?? false,
    retry: false,
    queryKey: ['saleTransactions', id, 'validatePublishToAtlas'],
    queryFn: async () => {
      if (!id) {
        throw new Error('Transaction ID is required');
      }

      return (
        await $httpRootApi.get<SaleTransactionAtlasValidationErrorResponse>(
          `transactions/sales/${id}/validate_publish_to_atlas`
        )
      ).data;
    },
  });

export const useValidateDisposalSalePublishQuery = (
  params: SaleTransactionValidatePublishPreCreateParams,
  opts?: UseQueryOptions<SaleTransactionAtlasValidationErrorResponse, SaleTransactionAtlasValidationErrorResponse>
) =>
  useQuery({
    ...opts,
    enabled: opts?.enabled ?? false,
    retry: false,
    onError: () => {
      // suppress error notification
    },
    queryKey: ['saleTransactions', 'validateDisposalPublish', params],
    queryFn: async () => {
      return (
        await $httpRootApi.get<SaleTransactionAtlasValidationErrorResponse>(
          `transactions/sales/validate_publish_to_atlas${getQueriesAsSearch(params)}`
        )
      ).data;
    },
  });

// ---- Bulk Mutations (Sales) ----

export const useSaleTransactionsBulkUpdateStatusMutation = (
  opts?: UseMutationOptions<SaleTransactionsBulkUpdateStatusPayload, any>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<SaleTransactionsBulkUpdateStatusPayload['body'], AxiosResponse<any>>(
          `/sale-transactions/bulk-change-status`,
          data.body
        )
      ).data,
  });

export const useSaleTransactionsBulkUpdateTagsMutation = (
  opts?: UseMutationOptions<SaleTransactionsBulkUpdateTagsPayload, any>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<SaleTransactionsBulkUpdateTagsPayload['body'], AxiosResponse<any>>(
          `/sale-transactions/bulk-update-tags`,
          data.body
        )
      ).data,
  });

export const useSaleTransactionsBulkDeleteMutation = (
  opts?: UseMutationOptions<SaleTransactionsBulkDeletePayload, any>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.delete<SaleTransactionsBulkDeletePayload['body'], AxiosResponse<any>>(
          `/sale-transactions/bulk`,
          { data: data.body }
        )
      ).data,
  });

export const useSaleTransactionsBulkUpdateTypeMutation = (
  opts?: UseMutationOptions<SaleTransactionsBulkUpdateTypePayload, any>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<SaleTransactionsBulkUpdateTypePayload['body'], AxiosResponse<any>>(
          `/sale-transactions/bulk-change-type`,
          data.body
        )
      ).data,
  });

export const useSaleTransactionsBulkChangeTenureMutation = (
  opts?: UseMutationOptions<SaleTransactionsBulkChangeTenurePayload, any>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<SaleTransactionsBulkChangeTenurePayload['body'], AxiosResponse<any>>(
          `/sale-transactions/bulk-change-tenure`,
          data.body
        )
      ).data,
  });

export const useSaleTransactionsBulkChangeBuildingTypesMutation = (
  opts?: UseMutationOptions<SaleTransactionsBulkChangeBuildingTypesPayload, any>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<SaleTransactionsBulkChangeBuildingTypesPayload['body'], AxiosResponse<any>>(
          `/sale-transactions/bulk-change-building-types`,
          data.body
        )
      ).data,
  });

export const useSaleTransactionsBulkSetOurAcquisitionMutation = (
  opts?: UseMutationOptions<SaleTransactionsBulkSetOurAcquisitionPayload, any>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<SaleTransactionsBulkSetOurAcquisitionPayload['body'], AxiosResponse<any>>(
          `/sale-transactions/bulk-set-our-acquisition`,
          data.body
        )
      ).data,
  });

export const useSaleTransactionsBulkSetOurDisposalMutation = (
  opts?: UseMutationOptions<SaleTransactionsBulkSetOurDisposalPayload, any>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<SaleTransactionsBulkSetOurDisposalPayload['body'], AxiosResponse<any>>(
          `/sale-transactions/bulk-set-our-disposal`,
          data.body
        )
      ).data,
  });

export const useSaleTransactionsBulkAssignUsersMutation = (
  opts?: UseMutationOptions<SaleTransactionsBulkAssignUsersPayload, any>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<SaleTransactionsBulkAssignUsersPayload['body'], AxiosResponse<any>>(
          `/sale-transactions/bulk-assign-users`,
          data.body
        )
      ).data,
  });

export const useSaleTransactionsBulkUpdateTeamsMutation = (
  opts?: UseMutationOptions<SaleTransactionsBulkUpdateTeamsPayload, any>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<SaleTransactionsBulkUpdateTeamsPayload['body'], AxiosResponse<any>>(
          `/sale-transactions/bulk-update-teams`,
          data.body
        )
      ).data,
  });

export const useSaleTransactionsBulkUpdateCompletionDateMutation = (
  opts?: UseMutationOptions<SaleTransactionsBulkUpdateCompletionDatePayload, any>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<SaleTransactionsBulkUpdateCompletionDatePayload['body'], AxiosResponse<any>>(
          `/sale-transactions/bulk-update-completion-date`,
          data.body
        )
      ).data,
  });

export const useSaleTransactionsBulkUpdateSectorsMutation = (
  opts?: UseMutationOptions<SaleTransactionsBulkUpdateSectorsPayload, any>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<SaleTransactionsBulkUpdateSectorsPayload['body'], AxiosResponse<any>>(
          `/sale-transactions/bulk-update-sectors`,
          data.body
        )
      ).data,
  });

export const useSaleTransactionsBulkUpdateGradeOfSpaceMutation = (
  opts?: UseMutationOptions<SaleTransactionsBulkUpdateGradeOfSpacePayload, any>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<SaleTransactionsBulkUpdateGradeOfSpacePayload['body'], AxiosResponse<any>>(
          `/sale-transactions/bulk-update-grade-of-space`,
          data.body
        )
      ).data,
  });

export const useSaleTransactionsBulkUpdateMeasurementTypeMutation = (
  opts?: UseMutationOptions<SaleTransactionsBulkUpdateMeasurementTypePayload, any>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<SaleTransactionsBulkUpdateMeasurementTypePayload['body'], AxiosResponse<any>>(
          `/sale-transactions/bulk-update-measurement-type`,
          data.body
        )
      ).data,
  });

export const useSaleTransactionsBulkUpdatePossibleUseClassesMutation = (
  opts?: UseMutationOptions<SaleTransactionsBulkUpdatePossibleUseClassesPayload, any>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<SaleTransactionsBulkUpdatePossibleUseClassesPayload['body'], AxiosResponse<any>>(
          `/sale-transactions/bulk-update-possible-use-classes`,
          data.body
        )
      ).data,
  });

export const useSaleTransactionsBulkPublishToAtlasMutation = (
  opts?: UseMutationOptions<SaleTransactionsBulkPublishToAtlasPayload, any>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<SaleTransactionsBulkPublishToAtlasPayload['body'], AxiosResponse<any>>(
          `/sale-transactions/bulk-publish-to-atlas`,
          data.body
        )
      ).data,
  });

export const useSaleTransactionReorderFilesMutation = (
  opts?: UseMutationOptions<SaleTransactionReorderFilesPayload, any>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<SaleTransactionReorderFilesPayload['body'], any>(
          `/transactions/sales/${data.id}/assets`,
          data.body
        )
      ).data,
  });

export const useSaleTransactionDeleteFileMutation = (
  opts?: UseMutationOptions<SaleTransactionDeleteFilePayload, any>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.delete<SaleTransactionDeleteFilePayload, any>(
          `/transactions/sales/${data.id}/assets/${data.assetId}`
        )
      ).data,
  });

export const useSaleTransactionRenameFileMutation = (
  opts?: UseMutationOptions<SaleTransactionRenameFilePayload, any>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<SaleTransactionRenameFilePayload['body'], any>(
          `/transactions/sales/${data.id}/assets/${data.assetId}`,
          data.body
        )
      ).data,
  });

// ---- Utils ----

export const updateSaleTransactionShowCache = (
  transactionId: string,
  callback: UpdateCacheCallback<SaleTransactionsShowItem>
) => updateQueryCache<SaleTransactionsShowItem>(saleTransactionsQueryKeys.show(transactionId).queryKey, callback);

export const updateAllSaleTransactionShowCaches = (
  transactionId: string,
  callback: UpdateCacheCallback<SaleTransactionsShowItem>
) => {
  queryClient.setQueriesData<SaleTransactionsShowItem>(
    saleTransactionsQueryKeys.show(transactionId).queryKey,
    (cache) => (cache ? callback(cache) : cache)
  );

  queryClient.invalidateQueries({
    queryKey: saleTransactionsQueryKeys.show(transactionId).queryKey,
    exact: true,
    refetchType: 'none',
  });
};

export const updateSaleTransactionShowCacheWithFields = (
  transactionId: string,
  update: Partial<SaleTransactionsShowItem>
) => {
  queryClient.setQueriesData<SaleTransactionsShowItem>(
    saleTransactionsQueryKeys.show(transactionId).queryKey,
    (item) => {
      if (!item) return item;

      const transaction = { ...item };

      if ('tasks' in update) {
        transaction.tasks = Array.isArray(update.tasks) ? update.tasks.slice() : [];
      }

      if ('comments' in update) {
        transaction.comments = update.comments ? update.comments.slice() : [];
      }

      if ('failed_bulk_action' in update) {
        transaction.failed_bulk_action = update.failed_bulk_action || transaction.failed_bulk_action;
      }

      if ('primary_address' in update) {
        transaction.primary_address = update.primary_address || transaction.primary_address;
      }

      if ('property' in update) {
        transaction.property = update.property || transaction.property;
      }

      if ('secondary_address' in update) {
        transaction.secondary_address = update.secondary_address || transaction.secondary_address;
      }

      if ('publish_to_radius' in update) {
        transaction.publish_to_radius = update.publish_to_radius
          ? update.publish_to_radius
          : transaction.publish_to_radius;
      }

      if ('publish_to_atlas' in update) {
        transaction.publish_to_atlas = update.publish_to_atlas ?? transaction.publish_to_atlas;
      }

      if ('tags' in update) {
        transaction.tags = update.tags ? update.tags.slice() : [];
      }

      // Atlas mandatory fields
      if ('completion_date' in update) {
        transaction.completion_date = update.completion_date ?? null;
      }

      if ('purchaser_agent_users' in update) {
        transaction.purchaser_agent_users = update.purchaser_agent_users ?? transaction.purchaser_agent_users;
      }

      if ('vendor_agent_users' in update) {
        transaction.vendor_agent_users = update.vendor_agent_users ?? transaction.vendor_agent_users;
      }

      // Atlas ranking fields
      if ('size_value' in update) {
        transaction.size_value = update.size_value !== undefined ? update.size_value : transaction.size_value;
      }

      if ('purchase_price' in update) {
        transaction.purchase_price =
          update.purchase_price !== undefined ? update.purchase_price : transaction.purchase_price;
      }

      if ('purchase_price_currency' in update) {
        transaction.purchase_price_currency = update.purchase_price_currency ?? transaction.purchase_price_currency;
      }

      if ('sale_type' in update) {
        transaction.sale_type = update.sale_type !== undefined ? update.sale_type : transaction.sale_type;
      }

      if ('size_metric' in update) {
        transaction.size_metric = update.size_metric ?? transaction.size_metric;
      }

      if ('building_types' in update) {
        transaction.building_types = update.building_types ?? transaction.building_types;
      }

      // Agency role fields
      if ('our_acquisition' in update) {
        transaction.our_acquisition = update.our_acquisition ?? transaction.our_acquisition;
      }

      if ('our_disposal' in update) {
        transaction.our_disposal = update.our_disposal ?? transaction.our_disposal;
      }

      // Space fields
      if ('entire_property' in update) {
        transaction.entire_property = update.entire_property ?? transaction.entire_property;
      }

      if ('leased_spaces' in update) {
        transaction.leased_spaces = update.leased_spaces ?? transaction.leased_spaces;
      }

      if ('size_measure' in update) {
        transaction.size_measure = update.size_measure ?? transaction.size_measure;
      }

      return transaction;
    }
  );
};

export const invalidateSaleTransactionsListQueries = () => {
  queryClient.invalidateQueries({
    queryKey: saleTransactionsQueryKeys.list._def,
    exact: false,
  });
};

export const invalidateSaleTransactionShowQuery = (transactionId: string) => {
  queryClient.invalidateQueries({
    queryKey: saleTransactionsQueryKeys.show(transactionId).queryKey,
    exact: true,
  });
};
